/* eslint-disable react-hooks/exhaustive-deps */
import { useContext, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  evaluateInitialRowsProperty,
  initializeGridFromDatasource,
} from '../form-designer/actions/runtime.actions';
import {
  FORM_KEYS,
  FORM_MODEL_ATTRIBUTES,
} from '../form-designer/constants/form-manager.constants';
import { FormContext } from '../form-designer/context';
import { getEntityDefinition, getEntityType } from '../form-designer/selectors/common.selectors';
import {
  getDataSource,
  getEntityTranslation,
  getEvaluatedExpression,
  getModelById,
} from '../form-designer/selectors/form-manager.selectors';
import { isHighVolumeGrid } from '../form-designer/selectors/ssGrid.selectors';
import {
  equalityFn,
  getDataType,
  getDecimalScale,
  getFormatType,
} from '../form-designer/utils/form-manager.utils';
import { logInteractionRulesEvaluationResult } from '../utils/log';
import { isNilOrEmpty } from '../utils/ramdaUtils';

export function usePrevious(value) {
  const ref = useRef();

  useEffect(() => {
    ref.current = value;
  });

  return ref.current;
}

export const useDidUpdateEffect = (func, deps) => {
  const didMount = useRef(false);

  useEffect(() => {
    if (didMount.current) {
      func();
    } else {
      didMount.current = true;
    }
  }, deps);
};

export const useFormSelector = (selector, props = {}, equalityFn) => {
  // create memoized selector for each instance
  const memoizedSelector = useMemo(() => selector, []);
  // inject formKey to every selector by default
  const formKey = useContext(FormContext);
  return useSelector((state) => memoizedSelector(state, { formKey, ...props }), equalityFn);
};

export const useSavedSearchSelector = (selector, props = {}, equalityFn) => {
  // create memoized selector for each instance
  const memoizedSelector = useMemo(() => selector, []);
  // inject selectedSavedSearchId to every selector by default
  return useSelector(
    (state) =>
      memoizedSelector(state, {
        selectedSavedSearchId: state.savedSearchDesigner.selectedSavedSearchId,
        ...props,
      }),
    equalityFn
  );
};

export const useInitializingGridFromDatasource = ({
  rowsLength,
  selectorProps,
  isVirtualComponent,
  isRebindCanvas,
  rebindCanvasLogInfo,
}) => {
  const dispatch = useDispatch();
  const formKey = useContext(FormContext);
  const { dataSourceKey } = useFormSelector(getModelById, { id: selectorProps.id });
  const dataSource = useFormSelector(getDataSource, { dataSourceKey }, equalityFn);

  const isDetail = formKey === FORM_KEYS.DETAIL;

  useEffect(() => {
    if (
      !isNilOrEmpty(dataSource) &&
      !rowsLength &&
      (isVirtualComponent || isDetail || isRebindCanvas)
    ) {
      dispatch(
        initializeGridFromDatasource({
          selectorProps,
          dataSource,
          formKey,
        })
      );

      if (isRebindCanvas) {
        logInteractionRulesEvaluationResult({ ...rebindCanvasLogInfo, index: selectorProps.index });
      }
    }
  }, [JSON.stringify(selectorProps.index), isRebindCanvas]);
};

export const useGridTitle = ({ id, index, entityName }) => {
  const evaluatedTitle = useFormSelector(getEvaluatedExpression, {
    id,
    index,
    entityName,
    prop: FORM_MODEL_ATTRIBUTES.TITLE,
  });
  const fallbackTitle = useFormSelector(getEntityTranslation, { id });

  return evaluatedTitle || fallbackTitle;
};

export const useAddingInitialRows = ({
  initialRows,
  selectorProps,
  isVirtualComponent,
  isRebindCanvas,
  rebindCanvasLogInfo,
}) => {
  const dispatch = useDispatch();
  const formKey = useContext(FormContext);
  const isHighVolumeGridData = useFormSelector(isHighVolumeGrid, {
    entityName: selectorProps.entityName,
  });
  const initialRowsCount = +initialRows;
  const isDetail = formKey === FORM_KEYS.DETAIL;

  useEffect(() => {
    if (
      initialRowsCount &&
      !isHighVolumeGridData &&
      (isVirtualComponent || isDetail || isRebindCanvas)
    ) {
      dispatch(evaluateInitialRowsProperty({ selectorProps, initialRowsCount, formKey }));

      if (isRebindCanvas) {
        logInteractionRulesEvaluationResult({ ...rebindCanvasLogInfo, index: selectorProps.index });
      }
    }
  }, [JSON.stringify(selectorProps.index), isRebindCanvas]);
};

export const useTableLastRowVisibilityEffect = ({
  tableRef,
  rows = [],
  isExpandableRowsTable = false,
}) => {
  useLayoutEffect(() => {
    const tableBody = tableRef?.current.querySelector('tbody');
    const lastTr = tableBody?.lastElementChild;

    if (!lastTr) {
      return;
    }

    if (!rows.length) {
      lastTr.style.display = 'table-row';
    } else if (
      (!isExpandableRowsTable && rows.length !== tableBody.children.length) ||
      (isExpandableRowsTable && tableBody?.children.length % 2 !== 0)
    ) {
      lastTr.style.display = 'none';
    }
  }, [tableRef, rows.length]);
};

export const useSsNumericTextBoxParams = ({ entityName, fieldName, value }) => {
  const entityDefinition = useFormSelector(getEntityDefinition);
  const entityType = useFormSelector(getEntityType);
  const dataType = useMemo(
    () => getDataType(entityDefinition, entityType, entityName, fieldName),
    []
  );
  const formatType = useMemo(
    () => getFormatType(entityDefinition, entityType, entityName, fieldName),
    []
  );
  const decimalScale = useMemo(() => getDecimalScale(dataType, formatType, value), [value]);

  return { dataType, decimalScale };
};
